/* The bowl builder (ported from Prototype D's portions builder) and bag helpers. Prices always come from @tikkas/shared, the same code the server runs. */
import { computeCheckout, priceBag, priceLine, resolveSizeIndex, rewardTier, type BagLine, type Flavour, type LineConfig, type LineReward, type Menu, type MenuItem, type Portion, type Protein, type Offer, type CheckoutTotals, type PricedLine, type RewardTier } from '@tikkas/shared';
import { uid } from './format.js';

export type ProteinChoice = 'chicken' | 'paneer' | 'half';

export interface Builder {
  itemId: string;
  sizeIndex: number;
  protein: ProteinChoice;
  portions: Portion[];
  toppingsOff: string[];
  sauces: string[];
  sauceTouched: boolean;
  upgradeIds: string[];
  meal: boolean;
  mealSide: string;
  mealDrink: string;
  membersDessert: boolean;
  qty: number;
  editId: string | null;
  reward: LineReward | null;
  autoLarge: boolean;
  open: { chicken: boolean; paneer: boolean };
  showUndo: boolean;
  /** Group order (docs/28): the item goes to this group's lines instead of the bag (Build.tsx). */
  group?: { token: string } | undefined;
}

export function newBuilder(itemId: string, reward: LineReward | null = null): Builder {
  return { itemId, sizeIndex: 0, protein: 'chicken', portions: [], toppingsOff: [], sauces: [], sauceTouched: false, upgradeIds: [], meal: false, mealSide: 'Chips', mealDrink: 'Coke', membersDessert: false, qty: 1, editId: null, reward, autoLarge: false, open: { chicken: true, paneer: false }, showUndo: false };
}

export function builderFromConfig(cfg: LineConfig, editId: string | null, qty: number): Builder {
  const b = newBuilder(cfg.itemId, cfg.reward ?? null);
  b.sizeIndex = cfg.sizeIndex;
  b.portions = cfg.portions.map((p) => ({ ...p }));
  b.toppingsOff = [...cfg.toppingsOff];
  b.sauces = [...cfg.sauces];
  b.sauceTouched = true;
  b.upgradeIds = [...cfg.upgradeIds];
  if (cfg.meal) {
    b.meal = true;
    b.mealSide = cfg.meal.side;
    b.mealDrink = cfg.meal.drink ?? 'Coke';
  }
  const ps = new Set(b.portions.map((p) => p.protein));
  b.protein = ps.size === 2 ? 'half' : ps.has('paneer') ? 'paneer' : 'chicken';
  b.open = { chicken: b.protein !== 'paneer', paneer: ps.has('paneer') };
  b.editId = editId;
  b.qty = qty;
  return b;
}

export function toConfig(b: Builder): LineConfig {
  const cfg: LineConfig = { itemId: b.itemId, sizeIndex: b.sizeIndex, portions: b.portions.map((p) => ({ ...p })), sauces: [...b.sauces] as LineConfig['sauces'], toppingsOff: [...b.toppingsOff] as LineConfig['toppingsOff'], upgradeIds: [...b.upgradeIds] };
  if (b.meal) cfg.meal = { side: b.mealSide, drink: b.mealDrink };
  if (b.reward) cfg.reward = b.reward;
  return cfg;
}

export const capOf = (item: MenuItem, b: Builder): number => item.sizes[b.sizeIndex]?.maxPortions ?? 0;
export const countOf = (b: Builder, f: Flavour, p: Protein): number => b.portions.filter((x) => x.flavour === f && x.protein === p).length;
const pkey = (x: Portion) => `${x.flavour}|${x.protein}`;
function nextProtein(b: Builder): Protein {
  return b.protein === 'paneer' ? 'paneer' : 'chicken';
}
function push(b: Builder, f: Flavour, p?: Protein) {
  b.portions.push({ flavour: f, protein: p ?? nextProtein(b) });
}
function removeOne(b: Builder, f: Flavour, p?: Protein): boolean {
  for (let i = b.portions.length - 1; i >= 0; i--) {
    const x = b.portions[i]!;
    if (x.flavour === f && (!p || x.protein === p)) {
      b.portions.splice(i, 1);
      return true;
    }
  }
  return false;
}
function mostCommon(b: Builder, except: string | null): string | null {
  let best: string | null = null, bn = 0;
  for (const k of new Set(b.portions.map(pkey))) {
    if (k === except) continue;
    const n = b.portions.filter((x) => pkey(x) === k).length;
    if (n > bn) {
      best = k;
      bn = n;
    }
  }
  return best;
}
function removeKey(b: Builder, k: string | null) {
  if (!k) return;
  const [f, p] = k.split('|') as [Flavour, Protein];
  removeOne(b, f, p);
}
export function canAutoLarge(item: MenuItem, b: Builder): boolean {
  return b.sizeIndex === 0 && item.sizes[1]?.maxPortions === 3 && !b.reward;
}
/** Anything left empty is filled with the first flavour when it goes in the bag. */
export function fillTo(item: MenuItem, b: Builder): void {
  const cap = capOf(item, b);
  while (b.portions.length && b.portions.length < cap) push(b, b.portions[0]!.flavour, b.portions[0]!.protein);
}
export type BuildStep = 'upsized' | 'full' | 'swapped' | 'downsized' | undefined;
/** First tap on a flavour fills every portion with it. + adds one (a third portion moves a Regular up to Large), − takes one away. */
export function flavAdd(item: MenuItem, b: Builder, f: Flavour, p: Protein): BuildStep {
  const cap = capOf(item, b);
  if (countOf(b, f, p)) return flavPlus(item, b, f, p);
  if (!b.portions.length) {
    for (let i = 0; i < cap; i++) push(b, f, p);
    return;
  }
  if (b.portions.length < cap) {
    push(b, f, p);
    return;
  }
  if (new Set(b.portions.map(pkey)).size < cap) {
    removeKey(b, mostCommon(b, null));
    push(b, f, p);
    return;
  }
  if (canAutoLarge(item, b)) {
    b.sizeIndex = 1;
    b.autoLarge = true;
    push(b, f, p);
    return 'upsized';
  }
  return 'full';
}
export function flavPlus(item: MenuItem, b: Builder, f: Flavour, p: Protein): BuildStep {
  if (b.portions.length < capOf(item, b)) {
    push(b, f, p);
    return;
  }
  if (canAutoLarge(item, b)) {
    b.sizeIndex = 1;
    b.autoLarge = true;
    push(b, f, p);
    return 'upsized';
  }
  const g = mostCommon(b, `${f}|${p}`);
  if (g) {
    removeKey(b, g);
    push(b, f, p);
    return 'swapped';
  }
  return 'full';
}
export function flavMinus(item: MenuItem, b: Builder, f: Flavour, p: Protein): BuildStep {
  removeOne(b, f, p);
  if (b.autoLarge && b.sizeIndex === 1 && b.portions.length <= 2) {
    b.sizeIndex = 0;
    b.autoLarge = false;
    return 'downsized';
  }
  return;
}
export function setSize(item: MenuItem, b: Builder, i: number): void {
  b.sizeIndex = i;
  b.autoLarge = false;
  const cap = capOf(item, b);
  if (!b.portions.length) return;
  while (b.portions.length < cap) push(b, b.portions[0]!.flavour, b.portions[0]!.protein);
  while (b.portions.length > cap) removeKey(b, mostCommon(b, null));
}
export function setProtein(b: Builder, p: ProteinChoice): void {
  b.protein = p;
  b.portions.forEach((x, i) => {
    x.protein = p === 'half' ? (i % 2 ? 'paneer' : 'chicken') : p;
  });
}

/* ---------- bag ---------- */

export interface Bag {
  lines: BagLine[];
}
export const newLine = (config: LineConfig, qty = 1): BagLine => ({ id: uid(), config, qty });

export function safePrice(menu: Menu, line: BagLine): PricedLine | null {
  try {
    return priceLine(menu, line);
  } catch {
    return null;
  }
}
export function sizeIndexFor(menu: Menu, cfg: LineConfig): number {
  const item = menu.items[cfg.itemId];
  if (!item) return cfg.sizeIndex;
  try {
    return resolveSizeIndex(item, cfg.sizeIndex, cfg.portions.length);
  } catch {
    return cfg.sizeIndex;
  }
}
export function bagCount(lines: BagLine[]): number {
  return lines.reduce((a, l) => a + l.qty, 0);
}
export function bagSubtotal(menu: Menu, lines: BagLine[]): number {
  try {
    return priceBag(menu, lines).subtotalPence;
  } catch {
    return lines.reduce((a, l) => a + (safePrice(menu, l)?.linePence ?? 0), 0);
  }
}
export function reservedPoints(lines: BagLine[], tiers?: readonly RewardTier[]): number {
  return lines.reduce((a, l) => a + (l.config.reward?.kind === 'points' ? rewardTier(l.config.reward.rewardId, tiers).points * l.qty : 0), 0);
}
/** The same sums the server makes, for instant display. The server's answer wins at checkout. */
export function clientTotals(menu: Menu, lines: BagLine[], offer: Offer | null, useCredit: boolean, creditPence: number, points: number): CheckoutTotals | null {
  try {
    return computeCheckout({ menu, lines, offer, useCredit, creditBalancePence: creditPence, pointsBalance: Math.max(points, reservedPoints(lines)) });
  } catch {
    return null;
  }
}
/** Which bag line a reward could be applied to. */
export function rewardTarget(menu: Menu, lines: BagLine[], rewardId: 'side' | 'main' | 'spin'): BagLine | undefined {
  return lines.find((l) => {
    if (l.config.reward || l.config.membersPrice) return false;
    const it = menu.items[l.config.itemId];
    if (!it) return false;
    if (rewardId === 'main') return it.hasTikkas;
    if (rewardId === 'side') return (it.category === 'sides' && sizeIndexFor(menu, l.config) === 0) || it.category === 'desserts';
    return it.category === 'desserts';
  });
}
